import type { DocumentNode } from 'graphql'
import { print } from 'graphql'
import { getBackendUrl } from 'utils/urls'

type KeepAliveGraphQLRequestOptions = {
  operationName: string
  variables: Record<string, any>
  query: DocumentNode
  token: string
}

/**
 * Makes a GraphQL request with keepalive enabled to ensure the request
 * completes even when the page is being unloaded.
 *
 * Useful for pagehide events where normal GraphQL mutations might not complete.
 *
 * @param options - The GraphQL request options
 */
export const makeKeepAliveGraphQLRequest = ({
  operationName,
  variables,
  query,
  token,
}: KeepAliveGraphQLRequestOptions): void => {
  const backendUrl = getBackendUrl()
  const useV2: string | null = localStorage.getItem('use-v2')
  const defaultBackendUrl = `${window.location.protocol}//${window.location.hostname}/${
    useV2 ? 'v2/graphql' : 'graphql'
  }`
  const url = backendUrl ?? defaultBackendUrl

  const request = {
    method: 'POST',
    body: JSON.stringify([
      {
        operationName,
        variables,
        query: print(query),
      },
    ]),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  }

  // Some browsers like firefox don't support keepalive, in that case we use service worker
  const browserSupportsKeepalive = 'keepalive' in new Request('')

  if (!browserSupportsKeepalive) {
    navigator?.serviceWorker?.controller?.postMessage({
      type: 'OutliveAysncApiRequest',
      request,
    })
    return
  }

  /**
   * https://developer.mozilla.org/en-US/docs/Web/API/fetch
   * The keepalive option can be used to allow the request to outlive the page.
   * Fetch with the keepalive flag is a replacement for the Navigator.sendBeacon() API.
   */
  fetch(url, {
    ...request,
    keepalive: true,
  })
}
